/**
 * Billing API — Phase 12 Plan 12-03 (STRP-01 + STRP-02 frontend half).
 *
 * Read-only consumer of the Plan 12-02 backend endpoints
 * (`/api/billing/admin/*`). Mirrors the established `src/lib/admin/`
 * pattern: thin async fetch wrappers + Zod runtime validation. Hooks live
 * in `useBilling.ts`; query keys live in `billingKeys.ts`.
 *
 * Backend contracts (from Plan 12-02 `handlers/platform/billing.rs`):
 *   GET /api/billing/admin/subscriptions/summary
 *   GET /api/billing/admin/subscriptions/events?limit=20
 *   GET /api/billing/admin/users/:id/payments
 *
 * All endpoints are admin-gated server-side (AdminUser extractor); we do
 * NOT re-check role in the SPA. Authentication is carried via cookies — we
 * pass `credentials: 'include'` so the request rides the existing session.
 */

import { z } from 'zod'

import { apiRequest } from '@/lib/api-client'

// ─── Schemas ────────────────────────────────────────────────────────────────

export const SubscriptionsSummarySchema = z.object({
  mrrCents: z.number().int(),
  activeCount: z.number().int(),
  failedCount: z.number().int(),
  currency: z.string(),
})
export type SubscriptionsSummary = z.infer<typeof SubscriptionsSummarySchema>

export const BillingEventSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  createdAt: z.number(), // Unix epoch seconds
  customerId: z.string().nullable(),
  amountCents: z.number().nullable(),
  status: z.string().nullable(),
  description: z.string().nullable(),
})
export type BillingEvent = z.infer<typeof BillingEventSchema>

export const BillingEventsResponseSchema = z.object({
  events: z.array(BillingEventSchema),
  total: z.number().int(),
})
export type BillingEventsResponse = z.infer<typeof BillingEventsResponseSchema>

export const PaymentHistoryItemSchema = z.object({
  id: z.string(),
  amountCents: z.number().int(),
  status: z.string(),
  createdAt: z.number(),
  invoiceId: z.string().nullable(),
  paymentIntentId: z.string().nullable(),
  description: z.string().nullable(),
})
export type PaymentHistoryItem = z.infer<typeof PaymentHistoryItemSchema>

export const PaymentHistoryResponseSchema = z.object({
  payments: z.array(PaymentHistoryItemSchema),
  total: z.number().int(),
})
export type PaymentHistoryResponse = z.infer<typeof PaymentHistoryResponseSchema>

// ─── Internal helpers ────────────────────────────────────────────────────────

/** Throw a typed Error from a non-2xx response, falling back to status text. */
async function throwBackendError(response: Response, fallback: string): Promise<never> {
  const data: { error?: string } = await response.json().catch(() => ({}))
  throw new Error(data.error || `${fallback} (${response.status})`)
}

const FETCH_OPTS: RequestInit = { credentials: 'include' }

// ─── API functions ───────────────────────────────────────────────────────────

/**
 * GET /api/billing/admin/subscriptions/summary
 *
 * Returns MRR + active/failed counts for the super-admin Subscriptions page.
 */
export async function fetchSubscriptionsSummary(): Promise<SubscriptionsSummary> {
  const response = await apiRequest(
    '/api/billing/admin/subscriptions/summary',
    FETCH_OPTS,
  )
  if (!response.ok) {
    await throwBackendError(response, 'Failed to load subscription summary')
  }
  return SubscriptionsSummarySchema.parse(await response.json())
}

/**
 * GET /api/billing/admin/subscriptions/events?limit=N
 *
 * Caps `limit` at 20 client-side as defense-in-depth (the backend also
 * caps at 20 — see `handlers/platform/billing.rs` `limit_clamping_caps_at_20`).
 */
export async function fetchSubscriptionsEvents(
  limit = 20,
): Promise<BillingEventsResponse> {
  const safeLimit = Math.max(1, Math.min(20, Math.floor(limit)))
  const response = await apiRequest(
    `/api/billing/admin/subscriptions/events?limit=${safeLimit}`,
    FETCH_OPTS,
  )
  if (!response.ok) {
    await throwBackendError(response, 'Failed to load recent events')
  }
  return BillingEventsResponseSchema.parse(await response.json())
}

/**
 * GET /api/billing/admin/users/:id/payments
 *
 * `userId` lives in the path segment (NOT a query param). Backend uses
 * `Path<uuid::Uuid>` so callers must pass a valid UUID; non-UUIDs return
 * a 400 from axum's extractor.
 */
export async function fetchUserPaymentHistory(
  userId: string,
): Promise<PaymentHistoryResponse> {
  const response = await apiRequest(
    `/api/billing/admin/users/${userId}/payments`,
    FETCH_OPTS,
  )
  if (!response.ok) {
    await throwBackendError(response, 'Failed to load payment history')
  }
  return PaymentHistoryResponseSchema.parse(await response.json())
}
